import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import ConfirmationModal from "components/ConfirmationModal";
import { getPurchaseDetailsApi, getAddressListApi } from "services/getApis";
import {
  declinePrescriptionApi,
  acceptPrescriptionApi,
  createAddressApi,
} from "services/postApis";
import { deleteAddressApi } from "services/deleteApis";
import { SuccessToast, ErrorToast, ErrorHandler } from "utils/toast";
import "styles/Prescription.css";

const STATIC_PRACTITIONER_NAME = "Dr. Lin Wei, L.Ac.";

const PRICING = {
  approve: { label: "$179", amount: 179 },
  decline: { label: "$99", amount: 99 },
};

const DECISION = {
  approve: "approve",
  decline: "decline",
};

const formatDeadline = (isoString) => {
  if (!isoString) return "";
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (e) {
    return isoString;
  }
};

const Prescription = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const submissionId = searchParams.get("submission") || searchParams.get("id");
  const hasFetchedRef = useRef(false);

  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [isDeclined, setIsDeclined] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [isSubmittingDecline, setIsSubmittingDecline] = useState(false);
  const [isSubmittingAccept, setIsSubmittingAccept] = useState(false);
  const [isDeletingAddress, setIsDeletingAddress] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  // Address selection state
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedAddressOption, setSelectedAddressOption] = useState("new");
  const [newAddress, setNewAddress] = useState({
    address: "",
    city: "",
    state: "",
    country: "United States",
    postalCode: "",
  });

  // Initial fetch for purchase details
  useEffect(() => {
    if (!submissionId) {
      navigate("/", { replace: true });
      return;
    }

    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getPurchaseDetailsApi(submissionId);
        if (res?.success && res?.payload) {
          setPurchaseDetails(res.payload);
        } else {
          const errMsg = res?.message || "Failed to fetch purchase details.";
          ErrorToast(errMsg, { toastId: "purchase-details-error" });
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error("Error fetching purchase details:", err);
        ErrorHandler(err, { toastId: "purchase-details-error" });
        navigate("/", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [submissionId, navigate]);

  // Fetch address list only when the user accepts and the UI changes
  useEffect(() => {
    if (!isAccepted) return;

    const fetchAddresses = async () => {
      setLoadingAddresses(true);
      try {
        const res = await getAddressListApi();
        if (res?.success && Array.isArray(res?.payload)) {
          const list = res.payload;
          setSavedAddresses(list);
          if (list.length > 0) {
            setSelectedAddressOption(list[0].id);
          } else {
            setSelectedAddressOption("new");
          }
        }
      } catch (err) {
        console.error("Error fetching address list:", err);
      } finally {
        setLoadingAddresses(false);
      }
    };

    fetchAddresses();
  }, [isAccepted]);

  if (!submissionId) {
    return null;
  }

  const handleDecisionClick = (decision) => {
    setPendingDecision(decision);
    setShowModal(true);
  };

  const handleConfirm = async () => {
    if (pendingDecision === DECISION.decline) {
      setIsSubmittingDecline(true);
      try {
        await declinePrescriptionApi(submissionId);
        setIsDeclined(true);
        setShowModal(false);
        setPendingDecision(null);
      } catch (err) {
        console.error("Failed to decline prescription:", err);
        ErrorHandler(err);
      } finally {
        setIsSubmittingDecline(false);
      }
    } else if (pendingDecision === DECISION.approve) {
      setIsAccepted(true);
      setShowModal(false);
      setPendingDecision(null);
    }
  };

  const handleCancel = () => {
    if (isSubmittingDecline) return;
    setShowModal(false);
    setPendingDecision(null);
  };

  // Delete address handlers
  const handleDeleteAddress = (e, addr) => {
    e.stopPropagation();
    setAddressToDelete(addr);
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteAddress = async () => {
    if (!addressToDelete) return;
    setIsDeletingAddress(true);
    try {
      if (addressToDelete.id) {
        await deleteAddressApi(addressToDelete.id);
      }
      SuccessToast("Address deleted successfully");
      setSavedAddresses((prev) => {
        const next = prev.filter((a) => a.id !== addressToDelete.id);
        if (selectedAddressOption === addressToDelete.id) {
          setSelectedAddressOption(next.length > 0 ? next[0].id : "new");
        }
        return next;
      });
      setShowDeleteModal(false);
      setAddressToDelete(null);
    } catch (err) {
      console.error("Failed to delete address:", err);
      ErrorHandler(err);
    } finally {
      setIsDeletingAddress(false);
    }
  };

  const handleCancelDeleteAddress = () => {
    if (isDeletingAddress) return;
    setShowDeleteModal(false);
    setAddressToDelete(null);
  };

  // Confirm order and accept prescription (saves address if new)
  const handleConfirmOrder = async () => {
    let targetAddressId = selectedAddressOption;

    setIsSubmittingAccept(true);
    try {
      if (selectedAddressOption === "new") {
        if (!newAddress.address.trim()) {
          ErrorToast("Please enter a street address.");
          setIsSubmittingAccept(false);
          return;
        }
        if (!newAddress.city.trim()) {
          ErrorToast("Please enter a city.");
          setIsSubmittingAccept(false);
          return;
        }
        if (!newAddress.state.trim()) {
          ErrorToast("Please enter a state.");
          setIsSubmittingAccept(false);
          return;
        }
        if (!newAddress.postalCode.trim()) {
          ErrorToast("Please enter a postal code.");
          setIsSubmittingAccept(false);
          return;
        }

        const payload = {
          address: newAddress.address.trim(),
          city: newAddress.city.trim(),
          state: newAddress.state.trim(),
          country: newAddress.country.trim() || "United States",
          postalCode: newAddress.postalCode.trim(),
        };

        const addrRes = await createAddressApi(payload);
        if (addrRes?.success || addrRes?.code === 201) {
          const createdAddr = addrRes?.payload || payload;
          targetAddressId = createdAddr.id;
        } else {
          const errMsg = addrRes?.message || "Failed to save address.";
          ErrorToast(errMsg);
          setIsSubmittingAccept(false);
          return;
        }
      }

      if (!targetAddressId || targetAddressId === "new") {
        ErrorToast("Please select or enter a shipping address.");
        setIsSubmittingAccept(false);
        return;
      }

      const res = await acceptPrescriptionApi({
        userSubmissionId: submissionId,
        addressId: targetAddressId,
      });
      if (res?.success || res?.code === 200 || res?.code === 201) {
        SuccessToast(res?.message || "Order confirmed successfully");
        setOrderSuccess(true);
      }
    } catch (err) {
      console.error("Failed to accept prescription:", err);
      ErrorHandler(err);
    } finally {
      setIsSubmittingAccept(false);
    }
  };

  const userName = purchaseDetails?.userName || user?.fullName || user?.name || "there";
  const userEmail = purchaseDetails?.userEmail || user?.email || "patient@example.com";
  const doctorName = purchaseDetails?.doctorName || STATIC_PRACTITIONER_NAME;
  const herbs = purchaseDetails?.herbs || [];
  const cardLast4 = purchaseDetails?.cardLast4 || "4242";
  const deadlineText = formatDeadline(purchaseDetails?.expiresAt);

  const selectedSavedAddress = savedAddresses.find((a) => a.id === selectedAddressOption);

  if (loading) {
    return (
      <div
        className="rr-page"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          color: "#fff",
        }}
      >
        <p>Loading recommendation details...</p>
      </div>
    );
  }

  // ── ORDER SUCCESS VIEW ──
  if (orderSuccess) {
    return (
      <div className="rr-page">
        <section className="rr-header-section">
          <div className="rr-container">
            <div className="rr-badge">
              <span className="rr-badge-text">Order Confirmed</span>
            </div>
            <h1 className="rr-greeting">
              Thank you, {userName}! <em>Your order is confirmed.</em>
            </h1>
            <p className="rr-greeting-sub">
              Your personalized herbal prescription will be compounded and dispatched shortly.
            </p>
          </div>
        </section>

        <section className="rr-accepted-section">
          <div className="rr-container">
            <div className="rr-success-card">
              <div className="rr-success-icon-badge" aria-hidden="true">✓</div>
              <h2 className="rr-success-title">Order Placed Successfully</h2>
              <p className="rr-success-desc">
                A confirmation receipt of <strong>$179</strong> has been charged to your card ending in <strong>•••• {cardLast4}</strong>. Tracking updates will be sent to <strong>{userEmail}</strong>.
              </p>
              <div className="rr-card-divider" style={{ width: "100%", margin: "16px 0" }} />
              <button
                id="rr-success-home-btn"
                className="rr-btn rr-btn--primary rr-btn--home"
                onClick={() => navigate("/")}
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </section>

        <section className="rr-support-section">
          <div className="rr-container">
            <p className="rr-support-text">
              Questions regarding your order?{" "}
              <a href="/contact" className="rr-support-link">
                Contact our care team
              </a>
              .
            </p>
          </div>
        </section>
      </div>
    );
  }

  // ── ACCEPTED / SHIPPING SELECTION VIEW ──
  if (isAccepted) {
    return (
      <div className="rr-page">
        {/* Header */}
        <section className="rr-header-section">
          <div className="rr-container">
            <div className="rr-badge">
              <span className="rr-badge-text">Delivery &amp; Confirmation</span>
            </div>
            <h1 className="rr-greeting">
              Confirm your <em>shipping details.</em>
            </h1>
            <p className="rr-greeting-sub">
              Please review your shipping address to finalize your herbal recommendation order.
            </p>
          </div>
        </section>

        {/* Accepted Form Card */}
        <section className="rr-accepted-section">
          <div className="rr-container">
            <div className="rr-accepted-card">
              {/* Section 1: Contact details (Disabled/Displayable only) */}
              <div>
                <h3 className="rr-section-heading">
                  <span className="rr-section-heading-icon">👤</span> Contact Information
                </h3>
                <div className="rr-form-row">
                  <div className="rr-form-field">
                    <label className="rr-form-label">
                      Full Name <span className="rr-disabled-tag">Verified</span>
                    </label>
                    <input
                      type="text"
                      className="rr-input-disabled"
                      value={userName}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="rr-form-field">
                    <label className="rr-form-label">
                      Email Address <span className="rr-disabled-tag">Verified</span>
                    </label>
                    <input
                      type="email"
                      className="rr-input-disabled"
                      value={userEmail}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="rr-card-divider" />

              {/* Section 2: Address Selection Cards */}
              <div>
                <h3 className="rr-section-heading">
                  <span className="rr-section-heading-icon">📍</span> Shipping Address
                </h3>
                <div className="rr-address-list">
                  {loadingAddresses && (
                    <p className="rr-address-location" style={{ fontStyle: "italic", margin: "4px 0 8px" }}>
                      Loading saved addresses...
                    </p>
                  )}
                  {/* Saved Address Cards */}
                  {savedAddresses.map((addr) => {
                    const isSelected = selectedAddressOption === addr.id;
                    return (
                      <div
                        key={addr.id}
                        className={`rr-address-card-item ${isSelected ? "rr-address-card-item--selected" : ""
                          }`}
                        onClick={() => setSelectedAddressOption(addr.id)}
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            setSelectedAddressOption(addr.id);
                          }
                        }}
                      >
                        <div className="rr-address-radio">
                          <div className="rr-address-dot" />
                        </div>
                        <div className="rr-address-details">
                          <div className="rr-address-header">
                            <span className="rr-address-recipient">{addr.address}</span>
                          </div>
                          <p className="rr-address-location">
                            {addr.city}, {addr.state} {addr.postalCode} • {addr.country}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="rr-address-delete-btn"
                          title="Delete address"
                          aria-label={`Delete address ${addr.address}`}
                          onClick={(e) => handleDeleteAddress(e, addr)}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}

                  {/* Card Option 2: Add New Address */}
                  <div
                    className={`rr-address-card-item ${selectedAddressOption === "new" ? "rr-address-card-item--selected" : ""
                      }`}
                    onClick={() => setSelectedAddressOption("new")}
                    role="radio"
                    aria-checked={selectedAddressOption === "new"}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setSelectedAddressOption("new");
                      }
                    }}
                  >
                    <div className="rr-address-radio">
                      <div className="rr-address-dot" />
                    </div>
                    <div className="rr-address-details">
                      <span className="rr-add-new-label">+ Add a new address</span>
                      <p className="rr-address-location">
                        Deliver to a different address
                      </p>
                    </div>
                  </div>

                  {/* Expandable New Address Inputs */}
                  {selectedAddressOption === "new" && (
                    <div className="rr-new-address-form">
                      <div className="rr-form-field">
                        <label className="rr-form-label">Street Address</label>
                        <input
                          type="text"
                          className="rr-input-text"
                          placeholder="e.g. 121 Pulaski Road"
                          value={newAddress.address}
                          onChange={(e) =>
                            setNewAddress({ ...newAddress, address: e.target.value })
                          }
                        />
                      </div>
                      <div className="rr-form-grid-2">
                        <div className="rr-form-field">
                          <label className="rr-form-label">City</label>
                          <input
                            type="text"
                            className="rr-input-text"
                            placeholder="e.g. Kings Park"
                            value={newAddress.city}
                            onChange={(e) =>
                              setNewAddress({ ...newAddress, city: e.target.value })
                            }
                          />
                        </div>
                        <div className="rr-form-field">
                          <label className="rr-form-label">State</label>
                          <input
                            type="text"
                            className="rr-input-text"
                            placeholder="e.g. NY"
                            value={newAddress.state}
                            onChange={(e) =>
                              setNewAddress({ ...newAddress, state: e.target.value })
                            }
                          />
                        </div>
                      </div>
                      <div className="rr-form-grid-2">
                        <div className="rr-form-field">
                          <label className="rr-form-label">Postal Code</label>
                          <input
                            type="text"
                            className="rr-input-text"
                            placeholder="e.g. 11754"
                            value={newAddress.postalCode}
                            onChange={(e) =>
                              setNewAddress({ ...newAddress, postalCode: e.target.value })
                            }
                          />
                        </div>
                        <div className="rr-form-field">
                          <label className="rr-form-label">Country</label>
                          <input
                            type="text"
                            className="rr-input-text"
                            placeholder="e.g. United States"
                            value={newAddress.country}
                            onChange={(e) =>
                              setNewAddress({ ...newAddress, country: e.target.value })
                            }
                          />
                        </div>
                      </div>

                    </div>
                  )}
                </div>
              </div>

              <div className="rr-card-divider" />

              {/* Section 3: Order Summary & Billing Recap */}
              <div className="rr-order-recap-box">
                <div className="rr-order-recap-header">
                  <div>
                    <h4 className="rr-order-recap-title">Personalized Herbal Formula</h4>
                    <p className="rr-order-recap-sub">
                      Prescribed formula compounding, practitioner review &amp; tracked delivery
                    </p>
                  </div>
                  <span className="rr-order-recap-total">{PRICING.approve.label}</span>
                </div>
                <div className="rr-recap-line-item">
                  <span>Payment Method</span>
                  <strong>Card ending in •••• {cardLast4}</strong>
                </div>
                <div className="rr-recap-line-item">
                  <span>Shipping Destination</span>
                  <strong>
                    {selectedSavedAddress
                      ? `${selectedSavedAddress.city}, ${selectedSavedAddress.state} ${selectedSavedAddress.postalCode}`
                      : newAddress.city
                        ? `${newAddress.city}, ${newAddress.state || "NY"} ${newAddress.postalCode}`
                        : "Please select a shipping address"}
                  </strong>
                </div>
              </div>

              {/* Actions */}
              <div className="rr-accepted-actions">
                <button
                  id="rr-confirm-order-btn"
                  className="rr-btn rr-btn--primary"
                  onClick={handleConfirmOrder}
                  disabled={isSubmittingAccept}
                >
                  {isSubmittingAccept
                    ? "Processing Order..."
                    : `Confirm & Ship My Formula — ${PRICING.approve.label}`}
                </button>
                <button
                  id="rr-back-btn"
                  className="rr-btn--secondary"
                  onClick={() => setIsAccepted(false)}
                  disabled={isSubmittingAccept}
                >
                  ← Back to recommendation details
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Support footer */}
        <section className="rr-support-section">
          <div className="rr-container">
            <p className="rr-support-text">
              Questions?{" "}
              <a href="/contact" className="rr-support-link">
                Contact us
              </a>
              .
            </p>
          </div>
        </section>

        {/* Delete Address Confirmation Modal */}
        <ConfirmationModal
          show={showDeleteModal}
          onCancel={handleCancelDeleteAddress}
          onConfirm={handleConfirmDeleteAddress}
          isLoading={isDeletingAddress}
          title="Delete Shipping Address?"
          description={
            addressToDelete
              ? `Are you sure you want to remove ${addressToDelete.address}, ${addressToDelete.city}?`
              : "Are you sure you want to remove this saved address?"
          }
          confirmText="Delete"
          cancelText="Cancel"
        />
      </div>
    );
  }


  if (isDeclined) {
    return (
      <div className="rr-page">
        {/* ── Page header ── */}
        <section className="rr-header-section">
          <div className="rr-container">
            <div className="rr-badge">
              <span className="rr-badge-text">Request Updated</span>
            </div>
            <h1 className="rr-greeting">
              Herbal purchase <em>declined.</em>
            </h1>
            <p className="rr-greeting-sub">
              Your response has been recorded. Below are the details regarding your
              consultation fee and account status.
            </p>
          </div>
        </section>

        {/* ── Declined summary card ── */}
        <section className="rr-declined-section">
          <div className="rr-container">
            <div className="rr-declined-card">
              <div className="rr-declined-header-row">
                <div className="rr-declined-icon-badge" aria-hidden="true">
                  ✕
                </div>
                <div>
                  <h2 className="rr-declined-title">Recommendation Declined</h2>
                  <p className="rr-declined-subtitle">
                    Intake reviewed by {doctorName}
                  </p>
                </div>
              </div>

              {/* Fee notice box */}
              <div className="rr-declined-fee-box">
                <div>
                  <p className="rr-fee-label">Consultation Fee Charge</p>
                  <p className="rr-fee-desc">
                    $99 will be automatically charged from your authorized card ending in •••• {cardLast4}.
                  </p>
                </div>
                <div className="rr-fee-amount">$99</div>
              </div>

              {/* Key details list */}
              <ul className="rr-declined-details-list">
                <li className="rr-declined-details-item">
                  <span className="rr-declined-item-dot" aria-hidden="true" />
                  <span>
                    <strong>No herbs shipped:</strong> Your personalised herbal formula will not be compounded or delivered.
                  </span>
                </li>
                <li className="rr-declined-details-item">
                  <span className="rr-declined-item-dot" aria-hidden="true" />
                  <span>
                    <strong>Automatic deduction:</strong> The $99 practitioner review fee is processed automatically from your card on file.
                  </span>
                </li>
                <li className="rr-declined-details-item">
                  <span className="rr-declined-item-dot" aria-hidden="true" />
                  <span>
                    <strong>No further charges:</strong> No recurring payments or additional fees will be charged for this intake session.
                  </span>
                </li>
              </ul>

              <div className="rr-card-divider" />

              {/* Action */}
              <div className="rr-declined-actions">
                <button
                  id="rr-go-home-btn"
                  className="rr-btn rr-btn--primary rr-btn--home"
                  onClick={() => navigate("/")}
                >
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Footer support link ── */}
        <section className="rr-support-section">
          <div className="rr-container">
            <p className="rr-support-text">
              Have questions or need assistance?{" "}
              <a href="/contact" className="rr-support-link">
                Contact us
              </a>
              .
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="rr-page">
      {/* ── Page header ── */}
      <section className="rr-header-section">
        <div className="rr-container">
          <div className="rr-badge">
            <span className="rr-badge-text">Herbal Recommendation</span>
          </div>
          <h1 className="rr-greeting">
            Hi {userName},{" "}
            <em>your herbal recommendation is ready.</em>
          </h1>
          <p className="rr-greeting-sub">
            Your practitioner has reviewed your intake and prepared a
            personalised formula for you. Please review the details below and
            let us know how you'd like to proceed.
          </p>
          {error && (
            <p className="rr-greeting-sub" style={{ color: "#ef4444", marginTop: "12px" }}>
              {error}
            </p>
          )}
        </div>
      </section>

      {/* ── Recommendation details card ── */}
      <section className="rr-details-section">
        <div className="rr-container">
          <div className="rr-details-card">
            {/* Practitioner */}
            <div className="rr-detail-row rr-practitioner-row">
              <div className="rr-practitioner-avatar" aria-hidden="true">
                🌿
              </div>
              <div>
                <p className="rr-detail-label">Prepared by</p>
                <p className="rr-detail-value rr-practitioner-name">
                  {doctorName}
                </p>
              </div>
            </div>

            <div className="rr-card-divider" />

            {/* Recommended herbs */}
            <div className="rr-detail-group">
              <p className="rr-detail-label">Recommended herbs</p>
              <ul className="rr-herb-list">
                {herbs.map((herb, i) => (
                  <li key={i} className="rr-herb-item">
                    <span className="rr-herb-dot" aria-hidden="true" />
                    {herb}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Decision deadline banner ── */}
      {deadlineText && (
        <section className="rr-deadline-section">
          <div className="rr-container">
            <div className="rr-deadline-banner">
              <span className="rr-deadline-icon" aria-hidden="true">🕐</span>
              <p className="rr-deadline-text">
                Please respond by{" "}
                <strong>{deadlineText}</strong> — 72 hours from
                delivery of this recommendation.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ── Decision cards ── */}
      <section className="rr-decision-section">
        <div className="rr-container">
          <div className="rr-decision-grid">
            {/* Card A — Approve */}
            <div className="rr-decision-card rr-decision-card--approve">
              <div className="rr-decision-card-icon" aria-hidden="true">✓</div>
              <h2 className="rr-decision-title">Approve &amp; ship my formula</h2>
              <p className="rr-decision-price">{PRICING.approve.label}</p>
              <p className="rr-decision-desc">
                Your practitioner's personalised formula will be compounded and
                shipped directly to you. Includes practitioner review, a custom
                herbal blend, and tracked delivery.
              </p>
              <ul className="rr-decision-benefits">
                <li>
                  <span className="rr-check" aria-hidden="true">✓</span>
                  Practitioner-reviewed formula
                </li>
                <li>
                  <span className="rr-check" aria-hidden="true">✓</span>
                  Personalised herbal blend
                </li>
                <li>
                  <span className="rr-check" aria-hidden="true">✓</span>
                  Shipped directly to your door
                </li>
              </ul>
              <button
                id="rr-approve-btn"
                className="rr-btn rr-btn--primary"
                onClick={() => handleDecisionClick(DECISION.approve)}
              >
                Approve — {PRICING.approve.label}
              </button>
            </div>

            {/* Card B — Decline */}
            <div className="rr-decision-card rr-decision-card--decline">
              <div className="rr-decision-card-icon rr-decision-card-icon--neutral" aria-hidden="true">✕</div>
              <h2 className="rr-decision-title">Decline herbal purchase</h2>
              <p className="rr-decision-price rr-decision-price--neutral">{PRICING.decline.label}</p>
              <p className="rr-decision-desc">
                You'll only be charged for the consultation fee. No herbal
                formula will be compounded or shipped. You can always return for
                a new recommendation in the future.
              </p>
              <ul className="rr-decision-benefits">
                <li>
                  <span className="rr-dash" aria-hidden="true">–</span>
                  Consultation fee only
                </li>
                <li>
                  <span className="rr-dash" aria-hidden="true">–</span>
                  No formula shipped
                </li>
                <li>
                  <span className="rr-dash" aria-hidden="true">–</span>
                  No further charges
                </li>
              </ul>
              <button
                id="rr-decline-btn"
                className="rr-btn rr-btn--outline"
                onClick={() => handleDecisionClick(DECISION.decline)}
              >
                Decline — {PRICING.decline.label} consultation fee
              </button>
            </div>
          </div>

          {/* Billing disclosure */}
          <p className="rr-billing-note">
            Your card ending in •••• {cardLast4} will be charged{" "}
            <strong>{PRICING.approve.label}</strong> if you approve, or{" "}
            <strong>{PRICING.decline.label}</strong> (consultation fee) if you
            decline. No charge has been made yet.
          </p>
        </div>
      </section>

      {/* ── Footer support link ── */}
      <section className="rr-support-section">
        <div className="rr-container">
          <p className="rr-support-text">
            Questions?{" "}
            <a href="/contact" className="rr-support-link">
              Contact us
            </a>{" "}
            or visit your{" "}
            <a href="/" className="rr-support-link">
              account dashboard
            </a>
            .
          </p>
        </div>
      </section>

      {/* ── Confirmation modal ── */}
      <ConfirmationModal
        show={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        isLoading={isSubmittingDecline}
        title="Confirm your choice?"
        description={
          pendingDecision === DECISION.approve
            ? `You're about to approve the recommendation ($${PRICING.approve.amount}). Your personalised formula will be prepared and shipped to you.`
            : `You're about to decline the herbal purchase. Only the $${PRICING.decline.amount} consultation fee will apply.`
        }
        confirmText="Confirm"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Prescription;

